"use client";

import CompanyLogo from "../Shared/Media/CompanyLogo";
import { contactData } from "@/data/Static/contact";
import HamburgerMenu from "../Navigation/HamburgerMenu";
import Link from "next/link";
import useScroll from "@/hooks/useScroll";
import { Toaster } from "sonner";
import DesktopNav from "../Navigation/DesktopNav";
import { Phone, MapPin, Mail } from "lucide-react";

export default function Header() {
  const isScrolled = useScroll();

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isScrolled 
          ? "bg-white/95 backdrop-blur-md shadow-xs h-16"
          : "bg-white h-20 lg:h-24"
      }`}
    >
      <div className="container mx-auto h-full px-4 lg:px-6">
        <nav className="flex justify-between items-center h-full gap-4">
          
          {/* Logo Section */}
          <Link href="/" className="flex items-center gap-3 group shrink-0">
            <CompanyLogo
              className={`transition-all duration-500 ${isScrolled ? "h-10 w-10" : "h-12 w-12 lg:h-14 lg:w-14"}`}
              fill="#4a5c1f"
            />
            <div className="flex flex-col">
              <span className={`font-serif font-bold uppercase tracking-widest text-hill-primary transition-all duration-500 ${
                isScrolled ? "text-base lg:text-lg" : "text-lg lg:text-2xl"
              }`}>
                Hill Medical
              </span>
              {!isScrolled && (
                <span className="text-[10px] lg:text-[11px] font-karla uppercase tracking-[0.2em] text-hill-primary font-bold transition-all duration-500">
                  Családi Fogászat
                </span>
              )}
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex flex-1 justify-center">
            <DesktopNav />
          </div>

          {/* Right Section: Contact & CTA */}
          <div className="flex items-center gap-4 lg:gap-6">
            {/* Desktop Contact Icons/Info */}
            <div className="hidden 2xl:flex items-center gap-6 text-hill-primary/70">
              <a href={`tel:${contactData.phone_href}`} className="flex items-center gap-2 hover:text-hill-secondary transition-colors group">
                <div className="w-8 h-8 rounded-full bg-hill-primary/5 flex items-center justify-center group-hover:bg-hill-secondary/10">
                  <Phone className="w-4 h-4" />
                </div>
                {!isScrolled && <span className="text-xs font-semibold truncate">{contactData.phone_display}</span>}
              </a>
              <a href={contactData.address_href} target="_blank" className="flex items-center gap-2 hover:text-hill-secondary transition-colors group">
                <div className="w-8 h-8 rounded-full bg-hill-primary/5 flex items-center justify-center group-hover:bg-hill-secondary/10">
                  <MapPin className="w-4 h-4" />
                </div>
                {!isScrolled && (
                  <div className="flex flex-col text-[10px] leading-tight font-semibold">
                    <span>{contactData.address.city}</span>
                    <span>{contactData.address.street}</span>
                  </div>
                )}
              </a>
            </div>

            {/* CTA Button */}
            <Link
              href="/kapcsolatfelvetel"
              className={`hidden sm:flex items-center justify-center px-4 lg:px-6 py-2 lg:py-2.5 bg-hill-secondary-strong text-white rounded-full font-bold transition-all duration-300 hover:bg-hill-secondary hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap ${
                isScrolled ? "text-xs px-4 py-2" : "text-sm"
              }`}
            >
              Online bejelentkezés
            </Link>

            {/* Mobile Hamburger Trigger */}
            <div className="xl:hidden">
              <HamburgerMenu />
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Quick Contacts Bar (Subtle) */}
      <div 
        className={`xl:hidden flex border-t bg-white/50 backdrop-blur-xs transition-all duration-500 overflow-hidden ${
          isScrolled ? "h-0 opacity-0" : "h-10 opacity-100"
        }`}
      >
        <div className="container mx-auto px-4 flex justify-around items-center h-full">
          <a href={`tel:${contactData.phone_href}`} className="flex items-center gap-2 text-hill-primary text-[10px] font-bold uppercase tracking-wider">
            <Phone className="w-3 h-3 text-hill-secondary" />
            Hívás
          </a>
          <a href={`mailto:${contactData.email}`} className="flex items-center gap-2 text-hill-primary text-[10px] font-bold uppercase tracking-wider border-x px-6">
            <Mail className="w-3 h-3 text-hill-secondary" />
            Email
          </a>
          <a href={contactData.address_href} target="_blank" className="flex items-center gap-2 text-hill-primary text-[10px] font-bold uppercase tracking-wider">
            <MapPin className="w-3 h-3 text-hill-secondary" />
            GPS
          </a>
        </div>
      </div>

      <Toaster position="top-center" />
    </header>
  );
}
